'use client';

import { logToDebugOverlay } from '@/helper/debugOverlayHelper';
import type { TrackingInformation } from '@/helper/tracking';
import { type StoreApi, create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface UsageData {
  postcode: string;
  annualConsumption: number;
  greenEnergy: boolean;
  startDate: string;
  city?: string;
  loadProfile?: string;
}

export interface LoadProfile {
  selectedLoadProfile: string;
}
export interface Tariff {
  selectedTariff: string;
}
export interface PersonalData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  postalCode: string;
  street: string;
  houseNumber: string;
  comments?: string;
}

export interface B2BStoreState {
  usageData: UsageData | null;
  lastProfile: LoadProfile | null;
  tariff: Tariff | null;
  personalData: PersonalData | null;
  trackingInformation?: TrackingInformation[];
  setUsageData: (data: UsageData) => void;
  updateLoadProfile: (profile: LoadProfile) => void;
  updateTariff: (tariff: Tariff) => void;
  updatePersonalData: (data: PersonalData) => void;
  updateTrackingInformation: (data: TrackingInformation[]) => void;

  clearB2BData: () => void;
}

const STORE_VERSION = 1;

export function createB2BStore(initState: Partial<B2BStoreState> = {}): StoreApi<B2BStoreState> {
  return create<B2BStoreState>()(
    persist(
      (set) => ({
        usageData: null,
        lastProfile: null,
        tariff: null,
        personalData: null,
        ...initState,

        setUsageData: (data) => {
          set(() => ({
            usageData: data,
          }));
        },

        updateLoadProfile: (profile) => {
          set({ lastProfile: profile });
        },

        updateTariff: (tariff) => {
          set({ tariff: tariff });
        },

        updatePersonalData: (data) => {
          set({ personalData: data });
        },

        updateTrackingInformation: (data) => {
          set({ trackingInformation: data });
        },

        clearB2BData: () => {
          set({ usageData: null, lastProfile: null, tariff: null, personalData: null });
        },
      }),
      {
        name: 'b2b-store',
        version: STORE_VERSION,
        migrate: (_persistedState, version) => {
          if (version !== STORE_VERSION) {
            logToDebugOverlay(
              'store-change',
              `Store version mismatch in B2BStore: ${version} !== ${STORE_VERSION}. Clearing store.`
            );
            return {
              usageData: null,
              lastProfile: null,
              tariff: null,
              personalData: null,
            } as any;
          }
          return _persistedState as any;
        },
      }
    )
  );
}

export type B2BStoreApi = ReturnType<typeof createB2BStore>;
